import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Package, FileText, Calendar, Wallet, User, LogOut, Loader2, ChevronRight,
  ShoppingBag, CheckCircle2, Truck, XCircle, Clock, Mail, ArrowLeft, CreditCard,
  Plus, Pencil, Trash2, MapPin,
} from 'lucide-react';
import {
  PortalOverview, PortalOrder, PortalInvoice, PortalBooking, PortalAddress, PortalCustomer, portalDisplayName,
  readPortalSession, writePortalSession, adoptPortalHandoff, requestPortalCode, verifyPortalCode,
  portalPasswordLogin, portalSignup, portalPasswordReset, scorePassword,
  portalLogout, fetchPortalOverview, createPortalPayLink, updatePortalProfile,
  savePortalAddress, deletePortalAddress, portalAddressToStructured,
  startWalletTopup, WalletTopupPromotion, PortalContainerBalance,
  PortalRental, cancelPortalRental,
} from '../../lib/portal';
import { StructuredAddress } from '../../lib/storefront';
import AddressForm, { emptyAddress } from '../../components/store/AddressForm';
import { supabase } from '../../lib/supabase';

// ─────────────────────────────────────────────────────────────────────────────
// Customer portal: per-business passwordless login + account dashboard.
// Tabs are modular — orders/bookings only appear when relevant to the
// business (water-delivery product businesses vs booking businesses).
// ─────────────────────────────────────────────────────────────────────────────

type TabId = 'orders' | 'rentals' | 'invoices' | 'bookings' | 'wallet' | 'account';

const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const moneyNum = (n: number | null | undefined) => `$${Number(n || 0).toFixed(2)}`;
const shortDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const ORDER_STATUS: Record<PortalOrder['status'], { label: string; className: string; icon: React.ElementType }> = {
  new: { label: 'Received', className: 'bg-blue-50 text-blue-700', icon: Clock },
  confirmed: { label: 'Confirmed', className: 'bg-indigo-50 text-indigo-700', icon: CheckCircle2 },
  out_for_delivery: { label: 'Out for delivery', className: 'bg-amber-50 text-amber-700', icon: Truck },
  delivered: { label: 'Delivered', className: 'bg-green-50 text-green-700', icon: CheckCircle2 },
  invoiced: { label: 'Completed', className: 'bg-green-50 text-green-700', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', className: 'bg-gray-100 text-gray-500', icon: XCircle },
};

/** Pickup orders share statuses with delivery, but read differently. */
function orderStatusMeta(order: PortalOrder) {
  const base = ORDER_STATUS[order.status] || ORDER_STATUS.new;
  if (order.fulfillment_type !== 'pickup') return base;
  if (order.status === 'out_for_delivery') return { ...base, label: 'Ready for pickup', icon: ShoppingBag };
  if (order.status === 'delivered') return { ...base, label: 'Picked up' };
  return base;
}

export default function CustomerPortal() {
  const { businessSlug } = useParams<{ businessSlug: string }>();
  const slug = businessSlug || '';
  const navigate = useNavigate();

  const [overview, setOverview] = useState<PortalOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [loggedIn, setLoggedIn] = useState<boolean>(() => {
    // Embeds on external sites pass their session in the URL fragment because
    // the browser partitions their iframe storage away from this tab.
    adoptPortalHandoff(slug);
    return !!readPortalSession(slug);
  });

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchPortalOverview(slug);
      setOverview(data);
      setLoggedIn(true);
    } catch (err) {
      if (err instanceof Error && err.message === 'unauthorized') {
        writePortalSession(slug, null);
        setLoggedIn(false);
        setOverview(null);
      }
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    if (readPortalSession(slug)) {
      loadOverview();
    } else {
      setLoading(false);
    }
  }, [slug, loadOverview]);

  const handleLogout = async () => {
    await portalLogout(slug);
    setOverview(null);
    setLoggedIn(false);
  };

  if (loading && !overview) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (!loggedIn || !overview) {
    return (
      <PortalLogin
        slug={slug}
        onLoggedIn={() => loadOverview()}
      />
    );
  }

  return (
    <PortalDashboard
      slug={slug}
      overview={overview}
      onLogout={handleLogout}
      onRefresh={loadOverview}
      onShop={() => navigate(`/${slug}/store`)}
    />
  );
}

// ── Login (email + password, with signup and recovery) ───────────────────────

type LoginView = 'login' | 'signup' | 'signup-verify' | 'recover' | 'recover-verify' | 'code' | 'code-verify';

function PasswordStrengthMeter({ password }: { password: string }) {
  if (!password) return null;
  const strength = scorePassword(password);
  return (
    <div className="mt-2">
      <div className="flex gap-1">
        {[1, 2, 3, 4].map((step) => (
          <div
            key={step}
            className="h-1 flex-1 rounded-full transition-colors"
            style={{ backgroundColor: step <= strength.score ? strength.color : '#e5e7eb' }}
          />
        ))}
      </div>
      <p className="mt-1 text-xs" style={{ color: strength.color }}>{strength.label}</p>
    </div>
  );
}

/** Public business branding for the pre-login screen (same anon read the booking page uses). */
function usePortalBranding(slug: string): { name: string; logoUrl: string | null } | null {
  const [brand, setBrand] = useState<{ name: string; logoUrl: string | null } | null>(null);
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    supabase
      .from('businesses')
      .select('name, booking_settings')
      .eq('slug', slug)
      .single()
      .then(({ data }) => {
        if (cancelled || !data) return;
        setBrand({
          name: data.name,
          logoUrl: (data.booking_settings as { logo_url?: string } | null)?.logo_url ?? null,
        });
        document.title = `${data.name} — Account`;
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);
  return brand;
}

function PortalLogin({ slug, onLoggedIn }: { slug: string; onLoggedIn: () => void }) {
  const brand = usePortalBranding(slug);
  const [view, setView] = useState<LoginView>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Staff exempted this email from verification — no code step (single-use).
  const [skipCode, setSkipCode] = useState(false);

  const go = (next: LoginView) => {
    setView(next);
    setError(null);
    setCode('');
    setConfirmPassword('');
    setSkipCode(false);
  };

  const passwordsMatch = password === confirmPassword;

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong — please try again');
    } finally {
      setBusy(false);
    }
  };

  const finish = (result: { token: string; customer: PortalCustomer }) => {
    writePortalSession(slug, {
      token: result.token,
      customerId: result.customer.id,
      displayName: portalDisplayName(result.customer),
    });
    onLoggedIn();
  };

  const doLogin = () => run(async () => finish(await portalPasswordLogin(slug, email.trim(), password)));
  const startSignup = () =>
    run(async () => {
      if (password.length < 8) throw new Error('Password must be at least 8 characters');
      if (!passwordsMatch) throw new Error('Passwords do not match');
      const { skipCode: exempt } = await requestPortalCode(slug, email.trim(), 'signup');
      if (exempt) {
        // Verification waived by the store — create the account directly.
        finish(await portalSignup(slug, email.trim(), '', password, firstName.trim(), lastName.trim()));
        return;
      }
      go('signup-verify');
    });
  const completeSignup = () =>
    run(async () => finish(await portalSignup(slug, email.trim(), code.trim(), password, firstName.trim(), lastName.trim())));
  const startRecover = () =>
    run(async () => {
      const { skipCode: exempt } = await requestPortalCode(slug, email.trim(), 'reset');
      go('recover-verify');
      setSkipCode(exempt);
    });
  const completeRecover = () =>
    run(async () => {
      if (password.length < 8) throw new Error('Password must be at least 8 characters');
      if (!passwordsMatch) throw new Error('Passwords do not match');
      finish(await portalPasswordReset(slug, email.trim(), skipCode ? '' : code.trim(), password));
    });
  const startCodeLogin = () =>
    run(async () => {
      await requestPortalCode(slug, email.trim());
      go('code-verify');
    });
  const completeCodeLogin = () => run(async () => finish(await verifyPortalCode(slug, email.trim(), code.trim())));

  const input = 'w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900';
  const primaryBtn = 'w-full mt-1 px-4 py-3 bg-gray-900 text-white text-sm font-semibold rounded-xl hover:bg-gray-800 disabled:opacity-50 transition-colors flex items-center justify-center gap-2';
  const linkBtn = 'font-semibold text-gray-900 hover:underline';

  const emailField = (
    <div className="relative">
      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email"
        className={`${input} !pl-9`}
        style={{ fontSize: '16px' }}
      />
    </div>
  );

  const codeField = (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      autoFocus
      maxLength={6}
      value={code}
      onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
      placeholder="6-digit code"
      className={`${input} text-center tracking-[0.5em] font-bold text-lg`}
    />
  );

  const isVerify = view === 'signup-verify' || view === 'recover-verify' || view === 'code-verify';

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          {brand?.logoUrl ? (
            <img
              src={brand.logoUrl}
              alt={brand.name}
              className="h-14 w-14 rounded-xl object-cover mx-auto mb-3 border border-gray-100"
            />
          ) : (
            <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-gray-900 mx-auto mb-3">
              {brand?.name ? (
                <span className="text-white text-lg font-bold">{brand.name.charAt(0)}</span>
              ) : (
                <User className="h-6 w-6 text-white" />
              )}
            </div>
          )}
          {brand?.name && (
            <p className="text-sm font-semibold text-gray-500 text-center mb-2">{brand.name}</p>
          )}
          <h1 className="text-xl font-bold text-gray-900 text-center">
            {view === 'login' && 'Login to my account'}
            {view === 'signup' && 'Create account'}
            {view === 'recover' && 'Recover password'}
            {view === 'code' && 'Email me a code'}
            {isVerify && (skipCode ? 'Set a new password' : 'Check your email')}
          </h1>
          <p className="text-sm text-gray-500 text-center mt-1 mb-6">
            {view === 'login' && 'Enter your e-mail and password:'}
            {view === 'signup' && 'We\u2019ll email you a code to confirm your address.'}
            {view === 'recover' && 'Enter your email and we\u2019ll send a code so you can set a new password.'}
            {view === 'code' && 'We\u2019ll email you a one-time code — no password needed.'}
            {isVerify && (skipCode
              ? 'Email verification is waived for your account — just choose a new password.'
              : `Enter the 6-digit code we sent to ${email.trim()}.`)}
          </p>

          {view === 'login' && (
            <form onSubmit={(e) => { e.preventDefault(); if (email.trim() && password) doLogin(); }} className="space-y-3">
              {emailField}
              <div>
                <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={input} style={{ fontSize: '16px' }} />
                <button type="button" onClick={() => go('recover')} className="mt-1.5 text-sm text-gray-600 hover:underline">
                  Forgot your password?
                </button>
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button type="submit" disabled={busy || !email.trim() || !password} className={primaryBtn}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {busy ? 'Signing in…' : 'Login'}
              </button>
              <div className="text-sm text-gray-500 text-center space-y-1.5 pt-2">
                <p>New customer? <button type="button" onClick={() => go('signup')} className={linkBtn}>Create your account</button></p>
                <p><button type="button" onClick={() => go('code')} className="text-gray-400 hover:text-gray-600 hover:underline">Email me a one-time login code instead</button></p>
              </div>
            </form>
          )}

          {view === 'signup' && (
            <form onSubmit={(e) => { e.preventDefault(); if (email.trim() && password.length >= 8 && passwordsMatch) startSignup(); }} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" className={input} style={{ fontSize: '16px' }} />
                <input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name" className={input} style={{ fontSize: '16px' }} />
              </div>
              {emailField}
              <div>
                <input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password (8+ characters)" className={input} style={{ fontSize: '16px' }} />
                <PasswordStrengthMeter password={password} />
              </div>
              <div>
                <input type="password" autoComplete="new-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm password" className={input} style={{ fontSize: '16px' }} />
                {confirmPassword && !passwordsMatch && <p className="mt-1 text-xs text-red-600">Passwords do not match</p>}
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button type="submit" disabled={busy || !email.trim() || password.length < 8 || !passwordsMatch} className={primaryBtn}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {busy ? 'Sending code…' : 'Create account'}
              </button>
              <p className="text-sm text-gray-500 text-center pt-1">
                Already have an account? <button type="button" onClick={() => go('login')} className={linkBtn}>Login</button>
              </p>
            </form>
          )}

          {(view === 'recover' || view === 'code') && (
            <form onSubmit={(e) => { e.preventDefault(); if (email.trim()) (view === 'recover' ? startRecover() : startCodeLogin()); }} className="space-y-3">
              {emailField}
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button type="submit" disabled={busy || !email.trim()} className={primaryBtn}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {busy ? 'Sending…' : 'Continue'}
              </button>
              <p className="text-sm text-gray-500 text-center pt-1">
                <button type="button" onClick={() => go('login')} className="text-gray-500 hover:text-gray-700 flex items-center gap-1 mx-auto">
                  <ArrowLeft className="h-3.5 w-3.5" /> Back to login
                </button>
              </p>
            </form>
          )}

          {isVerify && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!skipCode && code.trim().length !== 6) return;
                if (view === 'recover-verify' && (password.length < 8 || !passwordsMatch)) return;
                if (view === 'signup-verify') completeSignup();
                else if (view === 'recover-verify') completeRecover();
                else completeCodeLogin();
              }}
              className="space-y-3"
            >
              {!skipCode && codeField}
              {view === 'recover-verify' && (
                <>
                  <div>
                    <input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password (8+ characters)" className={input} style={{ fontSize: '16px' }} />
                    <PasswordStrengthMeter password={password} />
                  </div>
                  <div>
                    <input type="password" autoComplete="new-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm new password" className={input} style={{ fontSize: '16px' }} />
                    {confirmPassword && !passwordsMatch && <p className="mt-1 text-xs text-red-600">Passwords do not match</p>}
                  </div>
                </>
              )}
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button type="submit" disabled={busy || (!skipCode && code.trim().length !== 6) || (view === 'recover-verify' && (password.length < 8 || !passwordsMatch))} className={primaryBtn}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {busy
                  ? 'Verifying…'
                  : view === 'signup-verify'
                  ? 'Confirm & create account'
                  : view === 'recover-verify'
                  ? 'Set password & sign in'
                  : 'Sign in'}
              </button>
              <div className="flex items-center justify-between pt-1">
                <button
                  type="button"
                  onClick={() => go(view === 'signup-verify' ? 'signup' : view === 'recover-verify' ? 'recover' : 'code')}
                  className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                >
                  <ArrowLeft className="h-3.5 w-3.5" /> Back
                </button>
                {!skipCode && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => (view === 'signup-verify' ? startSignup() : view === 'recover-verify' ? startRecover() : startCodeLogin())}
                    className="text-sm font-semibold text-gray-900 hover:underline disabled:text-gray-400 disabled:no-underline"
                  >
                    Resend code
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────

function PortalDashboard({
  slug, overview, onLogout, onRefresh, onShop,
}: {
  slug: string;
  overview: PortalOverview;
  onLogout: () => void;
  onRefresh: () => void;
  onShop: () => void;
}) {
  const { business, profile, orders, invoices, bookings, walletTransactions } = overview;

  useEffect(() => {
    document.title = `${business.name} — Account`;
  }, [business.name]);

  const walletBalance = Number(profile.wallet || 0);
  // Wallet tab appears when the business enables it; an existing balance stays
  // visible even if the business later turns the feature off.
  const showWallet = business.walletEnabled === true || walletBalance > 0;

  const rentals = overview.rentals || [];

  const tabs = useMemo(() => {
    const list: Array<{ id: TabId; label: string; icon: React.ElementType }> = [];
    if (orders.length > 0 || business.storeEnabled) list.push({ id: 'orders', label: 'Orders', icon: Package });
    if (rentals.length > 0) list.push({ id: 'rentals', label: 'Rentals', icon: Clock });
    if (bookings.length > 0) list.push({ id: 'bookings', label: 'Bookings', icon: Calendar });
    list.push({ id: 'invoices', label: 'Invoices', icon: FileText });
    if (showWallet) list.push({ id: 'wallet', label: 'Wallet', icon: Wallet });
    list.push({ id: 'account', label: 'Account', icon: User });
    return list;
  }, [orders.length, rentals.length, bookings.length, business.storeEnabled, showWallet]);

  // Returning from a Stripe wallet top-up: land on the Wallet tab and re-fetch
  // shortly after so the webhook-credited balance shows up.
  const [returnedFromTopup] = useState(() => new URLSearchParams(window.location.search).get('wallet_topup') === 'success');
  // Deep link from the embed widget (e.g. ?tab=wallet), honored only if visible.
  const [requestedTab] = useState(() => new URLSearchParams(window.location.search).get('tab'));
  const [tab, setTab] = useState<TabId>(() => {
    if (returnedFromTopup && showWallet) return 'wallet';
    const requested = tabs.find(t => t.id === requestedTab);
    return requested ? requested.id : tabs[0].id;
  });

  useEffect(() => {
    if (!returnedFromTopup) return;
    const t1 = setTimeout(onRefresh, 2500);
    const t2 = setTimeout(onRefresh, 7000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnedFromTopup]);

  const customerName = [profile.first_name, profile.last_name].filter(Boolean).join(' ') || profile.email || 'there';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            {business.logoUrl ? (
              <img src={business.logoUrl} alt={business.name} className="h-9 w-9 rounded-lg object-cover" />
            ) : (
              <div className="h-9 w-9 rounded-lg bg-gray-900 flex items-center justify-center text-white text-sm font-bold">
                {business.name.charAt(0)}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-sm font-bold text-gray-900 truncate">{business.name}</div>
              <div className="text-xs text-gray-500 truncate">Hi, {customerName}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {business.storeEnabled && (
              <button
                onClick={onShop}
                className="hidden sm:flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-white bg-gray-900 rounded-xl hover:bg-gray-800 transition-colors"
              >
                <ShoppingBag className="h-4 w-4" /> Shop
              </button>
            )}
            <button
              onClick={onLogout}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 rounded-xl hover:bg-gray-100 transition-colors"
            >
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <nav className="flex gap-1 overflow-x-auto -mb-px">
            {tabs.map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-3.5 py-2.5 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${
                  tab === t.id
                    ? 'border-gray-900 text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <t.icon className="h-4 w-4" /> {t.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {tab === 'orders' && (
          <>
            <RentalsBanner rentals={rentals} onView={() => setTab('rentals')} />
            <ContainersCard containers={overview.containers || []} />
            <OrdersTab orders={orders} storeEnabled={business.storeEnabled} onShop={onShop} />
          </>
        )}
        {tab === 'rentals' && <RentalsTab slug={slug} rentals={rentals} onChanged={onRefresh} />}
        {tab === 'invoices' && <InvoicesTab slug={slug} invoices={invoices} onlinePayments={business.onlinePaymentsAvailable} />}
        {tab === 'bookings' && <BookingsTab bookings={bookings} />}
        {tab === 'wallet' && showWallet && (
          <WalletTab
            slug={slug}
            balance={walletBalance}
            transactions={walletTransactions}
            topupEnabled={business.walletTopupEnabled === true}
            promotions={overview.walletPromotions || []}
          />
        )}
        {tab === 'account' && <AccountTab slug={slug} profile={profile} addresses={overview.addresses || []} onSaved={onRefresh} />}
      </main>
    </div>
  );
}

// ── Rentals (recurring equipment like water dispensers) ──────────────────────

const rentalRateLabel = (r: PortalRental) =>
  `$${(r.rate_cents / 100).toFixed(2)}/${r.billing_interval === 'week' ? 'week' : 'month'}${r.quantity > 1 ? ' each' : ''}`;
const rentalBillDate = (d: string | null) =>
  d ? new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { month: 'long', day: 'numeric' }) : 'period end';
const isLiveRental = (r: PortalRental) =>
  r.status === 'active' || r.status === 'past_due' || r.status === 'pending';

const PORTAL_RENTAL_STATUS: Record<PortalRental['status'], { label: string; className: string }> = {
  pending: { label: 'Starting soon', className: 'bg-amber-50 text-amber-700' },
  active: { label: 'Active', className: 'bg-green-50 text-green-700' },
  past_due: { label: 'Payment due', className: 'bg-red-50 text-red-700' },
  canceled: { label: 'Canceled', className: 'bg-gray-100 text-gray-500' },
  ended: { label: 'Ended', className: 'bg-gray-100 text-gray-500' },
};

/** Compact pointer on the Orders tab — the full detail lives on the Rentals tab. */
function RentalsBanner({ rentals, onView }: { rentals: PortalRental[]; onView: () => void }) {
  const live = rentals.filter(isLiveRental);
  if (live.length === 0) return null;
  const units = live.reduce((n, r) => n + r.quantity, 0);
  const pastDue = live.some(r => r.status === 'past_due');
  return (
    <button
      onClick={onView}
      className={`mb-4 w-full flex items-center justify-between gap-3 rounded-2xl border p-4 sm:p-5 text-left transition-colors ${
        pastDue ? 'bg-red-50 border-red-200 hover:bg-red-100' : 'bg-white border-gray-200 hover:bg-gray-50'
      }`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <Clock className={`h-5 w-5 flex-shrink-0 ${pastDue ? 'text-red-500' : 'text-gray-500'}`} />
        <div className="min-w-0">
          <p className="text-sm font-bold text-gray-900">
            {units} rental{units !== 1 ? 's' : ''} {pastDue ? '— payment needed' : 'active'}
          </p>
          <p className="text-xs text-gray-500 truncate">
            {live.map(r => `${r.quantity} × ${r.item?.name || 'Rental'}`).join(' · ')}
          </p>
        </div>
      </div>
      <span className="flex items-center gap-1 text-xs font-semibold text-gray-600 flex-shrink-0">
        Manage <ChevronRight className="h-4 w-4" />
      </span>
    </button>
  );
}

/** Full Rentals tab: what's rented, what it costs, when it bills, cancel. */
function RentalsTab({ slug, rentals, onChanged }: {
  slug: string;
  rentals: PortalRental[];
  onChanged: () => void;
}) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const live = rentals.filter(isLiveRental);
  const past = rentals.filter(r => !isLiveRental(r));
  const monthlyCents = live
    .filter(r => r.status !== 'pending')
    .reduce((n, r) => n + r.rate_cents * r.quantity * (r.billing_interval === 'week' ? 4.33 : 1), 0);

  const toggle = async (r: PortalRental, cancel: boolean) => {
    setBusyId(r.id);
    setError(null);
    try {
      await cancelPortalRental(slug, r.id, cancel);
      setConfirmId(null);
      onChanged();
    } catch {
      setError('Could not update the rental — please try again.');
    } finally {
      setBusyId(null);
    }
  };

  if (rentals.length === 0) {
    return <EmptyState icon={Clock} title="No rentals" body="Equipment you rent will show up here with its billing schedule." />;
  }

  return (
    <div className="space-y-4">
      {/* Recurring total up top so the cost is never a surprise */}
      {live.length > 0 && monthlyCents > 0 && (
        <div className="bg-gray-900 text-white rounded-2xl p-4 sm:p-5 flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-300 font-medium">Your recurring rental total</p>
            <p className="text-2xl font-bold mt-0.5">{money(Math.round(monthlyCents))}<span className="text-sm font-medium text-gray-300">/month</span></p>
          </div>
          <Clock className="h-8 w-8 text-gray-500" />
        </div>
      )}

      {live.map(r => {
        const status = PORTAL_RENTAL_STATUS[r.status];
        return (
          <div key={r.id} className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-base font-bold text-gray-900">
                  {r.quantity} × {r.item?.name || 'Rental'}
                </h3>
                <p className="text-sm text-gray-600 mt-0.5">{rentalRateLabel(r)}</p>
              </div>
              <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${status.className}`}>
                {r.cancel_at_period_end && r.status === 'active' ? 'Ending soon' : status.label}
              </span>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-gray-400 font-medium">Renting since</p>
                <p className="text-gray-900 font-medium">{r.started_at ? shortDate(r.started_at) : '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 font-medium">
                  {r.cancel_at_period_end ? 'Ends on' : 'Next bill'}
                </p>
                <p className={`font-medium ${r.cancel_at_period_end ? 'text-amber-700' : 'text-gray-900'}`}>
                  {r.status === 'pending' ? 'After delivery' : rentalBillDate(r.next_bill_date)}
                </p>
              </div>
            </div>

            {r.status === 'pending' && (
              <p className="mt-3 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                Your rental starts when the order is delivered — the first period is billed with that delivery.
              </p>
            )}
            {r.status === 'past_due' && (
              <p className="mt-3 text-xs text-red-700 bg-red-50 rounded-lg px-3 py-2">
                A rental payment didn't go through. Pay the open invoice on the Invoices tab to keep your rental active.
              </p>
            )}
            {r.cancel_at_period_end && (r.status === 'active' || r.status === 'past_due') && (
              <p className="mt-3 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                Cancellation scheduled — your rental ends on {rentalBillDate(r.next_bill_date)} and you won't be billed again. Equipment gets picked up then.
              </p>
            )}

            {(r.status === 'active' || r.status === 'past_due') && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                {r.cancel_at_period_end ? (
                  <button
                    onClick={() => toggle(r, false)}
                    disabled={busyId === r.id}
                    className="text-sm font-semibold text-gray-900 underline underline-offset-2 disabled:opacity-50"
                  >
                    {busyId === r.id ? 'Saving…' : 'Keep my rental'}
                  </button>
                ) : confirmId === r.id ? (
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-xs text-gray-600">
                      End this rental on {rentalBillDate(r.next_bill_date)}?
                    </span>
                    <button
                      onClick={() => toggle(r, true)}
                      disabled={busyId === r.id}
                      className="text-sm font-semibold text-red-600 underline underline-offset-2 disabled:opacity-50"
                    >
                      {busyId === r.id ? 'Saving…' : 'Yes, cancel'}
                    </button>
                    <button
                      onClick={() => setConfirmId(null)}
                      className="text-sm font-medium text-gray-500 underline underline-offset-2"
                    >
                      Keep it
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmId(r.id)}
                    className="text-sm font-medium text-gray-500 underline underline-offset-2 hover:text-gray-700"
                  >
                    Cancel rental
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}

      {error && <p className="text-xs text-red-600">{error}</p>}

      {past.length > 0 && (
        <div>
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2 mt-6">Past rentals</p>
          <div className="space-y-2">
            {past.map(r => (
              <div key={r.id} className="bg-white rounded-xl border border-gray-200 px-4 py-3 flex items-center justify-between gap-3 opacity-70">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-700 truncate">
                    {r.returned_quantity || r.quantity} × {r.item?.name || 'Rental'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {rentalRateLabel(r)}{r.ended_at ? ` · ended ${shortDate(r.ended_at)}` : ''}
                  </p>
                </div>
                <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold flex-shrink-0 ${PORTAL_RENTAL_STATUS[r.status].className}`}>
                  {PORTAL_RENTAL_STATUS[r.status].label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Containers on hand (bottle deposits) ─────────────────────────────────────

function ContainersCard({ containers }: { containers: PortalContainerBalance[] }) {
  const held = containers.filter(c => c.quantity > 0);
  if (held.length === 0) return null;
  const totalDeposits = held.reduce((n, c) => n + Math.max(0, c.deposit_cents_held), 0);
  const groups = new Map<string, { label: string; rows: PortalContainerBalance[] }>();
  for (const c of held) {
    const key = c.customer_address_id || 'unassigned';
    const label = c.customer_address_id ? (c.address_label || 'Delivery address') : 'Not tied to an address';
    const group = groups.get(key) || { label, rows: [] };
    group.rows.push(c);
    groups.set(key, group);
  }
  const grouped = groups.size > 1 || held.some(c => c.customer_address_id);
  return (
    <div className="mb-4 bg-white rounded-2xl border border-gray-200 p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-2">
        <Package className="h-4 w-4 text-gray-500" />
        <h3 className="text-sm font-bold text-gray-900">Containers on hand</h3>
      </div>
      {grouped ? (
        <div className="space-y-3 mb-2">
          {Array.from(groups.entries()).map(([key, group]) => (
            <div key={key}>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">{group.label}</p>
              <div className="flex flex-wrap gap-2">
                {group.rows.map(c => (
                  <span key={`${key}-${c.stock_item_id}`} className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                    {c.quantity} × {c.name}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2 mb-2">
          {held.map(c => (
            <span key={c.stock_item_id} className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
              {c.quantity} × {c.name}
            </span>
          ))}
        </div>
      )}
      <p className="text-xs text-gray-500">
        ${(totalDeposits / 100).toFixed(2)} in refundable deposits — leave your empties out at delivery
        (or on your final pickup) and the deposit comes back to you.
      </p>
    </div>
  );
}

// ── Orders tab ────────────────────────────────────────────────────────────────

function OrdersTab({ orders, storeEnabled, onShop }: { orders: PortalOrder[]; storeEnabled: boolean; onShop: () => void }) {
  const [expanded, setExpanded] = useState<string | null>(null);

  if (orders.length === 0) {
    return (
      <EmptyState
        icon={Package}
        title="No orders yet"
        body="When you place an order, it will show up here so you can track it."
        action={storeEnabled ? { label: 'Browse products', onClick: onShop } : undefined}
      />
    );
  }

  return (
    <div className="space-y-3">
      {storeEnabled && (
        <button
          onClick={onShop}
          className="w-full sm:hidden flex items-center justify-center gap-2 px-4 py-3 bg-gray-900 text-white text-sm font-semibold rounded-xl"
        >
          <ShoppingBag className="h-4 w-4" /> Place a new order
        </button>
      )}
      {orders.map(order => {
        const status = orderStatusMeta(order);
        const isOpen = expanded === order.id;
        return (
          <div key={order.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <button
              onClick={() => setExpanded(isOpen ? null : order.id)}
              className="w-full px-4 py-3.5 flex items-center justify-between gap-3 text-left"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold text-gray-900">{order.order_number}</span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${status.className}`}>
                    <status.icon className="h-3 w-3" /> {status.label}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {shortDate(order.placed_at)} · {order.items.length} item{order.items.length === 1 ? '' : 's'} · {order.fulfillment_type === 'delivery' ? 'Delivery' : 'Pickup'}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-sm font-bold text-gray-900">{money(order.estimated_total_cents)}</span>
                <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
              </div>
            </button>
            {isOpen && (
              <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                <div className="space-y-1.5">
                  {order.items.map(item => (
                    <div key={item.id} className="flex justify-between text-sm">
                      <span className="text-gray-700">{item.product_name} × {Number(item.quantity)}</span>
                      <span className="text-gray-900 font-medium">{moneyNum(Number(item.unit_price) * Number(item.quantity))}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100 space-y-1 text-sm">
                  <Row label="Subtotal" value={money(order.estimated_subtotal_cents)} />
                  {order.estimated_discount_cents > 0 && <Row label="Discount" value={`-${money(order.estimated_discount_cents)}`} />}
                  <Row label="Tax" value={money(order.estimated_tax_cents)} />
                  <Row label="Total" value={money(order.estimated_total_cents)} bold />
                </div>
                {order.delivery_address && (
                  <p className="mt-3 text-xs text-gray-500">Delivering to: {order.delivery_address}</p>
                )}
                {order.requested_date && (
                  <p className="mt-1 text-xs text-gray-500">
                    Requested: {shortDate(order.requested_date)}{order.requested_window ? ` · ${order.requested_window}` : ''}
                  </p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Invoices tab ──────────────────────────────────────────────────────────────

function InvoicesTab({ slug, invoices, onlinePayments }: { slug: string; invoices: PortalInvoice[]; onlinePayments: boolean }) {
  const [payingId, setPayingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const payNow = async (invoice: PortalInvoice) => {
    setPayingId(invoice.id);
    setError(null);
    try {
      const token = await createPortalPayLink(slug, invoice.id);
      window.location.href = `/pay-invoice/${token}`;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not open the payment page');
      setPayingId(null);
    }
  };

  if (invoices.length === 0) {
    return <EmptyState icon={FileText} title="No invoices yet" body="Invoices from your orders and services will appear here." />;
  }

  return (
    <div className="space-y-3">
      {error && <p className="text-sm text-red-600">{error}</p>}
      {invoices.map(invoice => {
        const due = invoice.amount_due_cents > 0;
        return (
          <div key={invoice.id} className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3.5 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-bold text-gray-900">{invoice.invoice_number}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                  due ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'
                }`}>
                  {due ? (invoice.amount_paid_cents > 0 ? 'Partially paid' : 'Payment due') : 'Paid'}
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {shortDate(invoice.finalized_at || invoice.created_at)} · Total {money(invoice.total_cents)}
                {due && ` · ${money(invoice.amount_due_cents)} due`}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {invoice.pdf_url && (
                <a
                  href={invoice.pdf_url}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-2 text-sm font-medium text-gray-600 rounded-xl hover:bg-gray-100 transition-colors"
                >
                  View PDF
                </a>
              )}
              {due && onlinePayments && (
                <button
                  onClick={() => payNow(invoice)}
                  disabled={payingId === invoice.id}
                  className="flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold text-white bg-gray-900 rounded-xl hover:bg-gray-800 disabled:opacity-50 transition-colors"
                >
                  {payingId === invoice.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                  Pay now
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Bookings tab ──────────────────────────────────────────────────────────────

function BookingsTab({ bookings }: { bookings: PortalBooking[] }) {
  if (bookings.length === 0) {
    return <EmptyState icon={Calendar} title="No bookings yet" body="Your appointments and services will appear here." />;
  }
  return (
    <div className="space-y-3">
      {bookings.map(booking => (
        <div key={booking.id} className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3.5 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-bold text-gray-900 truncate">
              {booking.title || `Booking #${booking.booking_number ?? ''}`}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {shortDate(booking.start_date)}
              {booking.start_time ? ` · ${booking.start_time.slice(0, 5)}` : ''}
              {(booking.booking_address || booking.location) ? ` · ${booking.booking_address || booking.location}` : ''}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-sm font-bold text-gray-900">{moneyNum(booking.total_amount)}</div>
            <div className="text-xs text-gray-500 capitalize">{String(booking.status).replace(/_/g, ' ')}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Wallet tab ────────────────────────────────────────────────────────────────

function WalletTab({ slug, balance, transactions, topupEnabled, promotions }: {
  slug: string;
  balance: number;
  transactions: PortalOverview['walletTransactions'];
  topupEnabled: boolean;
  promotions: WalletTopupPromotion[];
}) {
  const [topupOpen, setTopupOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set when returning from Stripe (?wallet_topup=success); the credit lands
  // via webhook so the balance may lag the redirect by a moment.
  const [justPaid] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('wallet_topup') !== 'success') return false;
    params.delete('wallet_topup');
    const query = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
    return true;
  });

  const amountCents = Math.round((parseFloat(amount) || 0) * 100);
  const activeBonus = promotions
    .filter(p => p.min_amount_cents <= amountCents)
    .reduce<WalletTopupPromotion | null>((best, p) => (!best || p.min_amount_cents > best.min_amount_cents ? p : best), null);

  const startTopup = async () => {
    if (busy || amountCents < 500) return;
    setBusy(true);
    setError(null);
    try {
      const base = `${window.location.origin}${window.location.pathname}`;
      const url = await startWalletTopup(slug, amountCents, `${base}?wallet_topup=success`, `${base}?wallet_topup=cancelled`);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start the payment — please try again');
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {justPaid && (
        <div className="rounded-xl bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
          Payment received — your balance will update in a moment. Refresh if you don't see it yet.
        </div>
      )}
      <div className="bg-gray-900 rounded-2xl p-6 text-white">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm text-gray-300">Wallet balance</div>
            <div className="text-3xl font-bold mt-1">{moneyNum(balance)}</div>
            <p className="text-xs text-gray-400 mt-2">
              Your balance is applied to future orders and invoices.
            </p>
          </div>
          {topupEnabled && !topupOpen && (
            <button
              onClick={() => setTopupOpen(true)}
              className="shrink-0 flex items-center gap-1.5 px-4 py-2.5 bg-white text-gray-900 text-sm font-semibold rounded-xl hover:bg-gray-100 transition-colors"
            >
              <Plus className="h-4 w-4" /> Add money
            </button>
          )}
        </div>

        {topupEnabled && topupOpen && (
          <div className="mt-5 pt-5 border-t border-white/15 space-y-3">
            {promotions.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {promotions.map(p => {
                  const active = activeBonus?.id === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => setAmount((p.min_amount_cents / 100).toFixed(2))}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                        active
                          ? 'bg-white text-gray-900 border-white'
                          : 'bg-white/10 text-white border-white/20 hover:bg-white/20'
                      }`}
                    >
                      Add {money(p.min_amount_cents)} → get {money(p.bonus_cents)} bonus
                    </button>
                  );
                })}
              </div>
            )}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                <input
                  type="number"
                  min="5"
                  step="0.01"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  placeholder="Amount (min $5)"
                  className="w-full pl-7 pr-3 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-white/40"
                  style={{ fontSize: '16px' }}
                />
              </div>
              <button
                onClick={startTopup}
                disabled={busy || amountCents < 500}
                className="px-4 py-2.5 bg-white text-gray-900 text-sm font-semibold rounded-xl hover:bg-gray-100 disabled:opacity-40 transition-colors flex items-center gap-2"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                Pay
              </button>
            </div>
            {activeBonus && amountCents >= 500 && (
              <p className="text-xs text-green-300 font-medium">
                You'll receive {money(amountCents + activeBonus.bonus_cents)} — includes a {money(activeBonus.bonus_cents)} bonus.
              </p>
            )}
            <p className="text-[11px] text-gray-400">
              Top-ups are paid by card at checkout.
            </p>
            {error && <p className="text-xs text-red-300">{error}</p>}
            <button onClick={() => setTopupOpen(false)} className="text-xs text-gray-400 hover:text-white">
              Cancel
            </button>
          </div>
        )}
      </div>
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Activity</h3>
        {transactions.length === 0 ? (
          <p className="text-sm text-gray-500">No wallet activity yet.</p>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-100">
            {transactions.map(txn => (
              <div key={txn.id} className="px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm text-gray-900 truncate">{txn.description || txn.type}</div>
                  <div className="text-xs text-gray-500">{shortDate(txn.created_at)}</div>
                </div>
                <span className={`text-sm font-bold shrink-0 ${Number(txn.amount) >= 0 ? 'text-green-600' : 'text-gray-900'}`}>
                  {Number(txn.amount) >= 0 ? '+' : ''}{moneyNum(txn.amount)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Account tab ───────────────────────────────────────────────────────────────

function AccountTab({ slug, profile, addresses, onSaved }: {
  slug: string;
  profile: PortalOverview['profile'];
  addresses: PortalAddress[];
  onSaved: () => void;
}) {
  const [firstName, setFirstName] = useState(profile.first_name || '');
  const [lastName, setLastName] = useState(profile.last_name || '');
  const [phone, setPhone] = useState(profile.phone || '');
  const [company, setCompany] = useState(profile.company || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await updatePortalProfile(slug, { firstName, lastName, phone, company });
      setSaved(true);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save your details');
    } finally {
      setSaving(false);
    }
  };

  const input = 'w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900';

  return (
    <div className="max-w-lg space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email</label>
          <input type="email" value={profile.email || ''} disabled className={`${input} bg-gray-50 text-gray-500`} />
          <p className="mt-1 text-xs text-gray-500">Your email is your login and can't be changed here.</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">First name</label>
            <input value={firstName} onChange={e => setFirstName(e.target.value)} className={input} style={{ fontSize: '16px' }} />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Last name</label>
            <input value={lastName} onChange={e => setLastName(e.target.value)} className={input} style={{ fontSize: '16px' }} />
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Phone</label>
          <input value={phone} onChange={e => setPhone(e.target.value)} className={input} style={{ fontSize: '16px' }} />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Company / business name</label>
          <input
            value={company}
            onChange={e => setCompany(e.target.value)}
            placeholder="Shown on your invoices (optional)"
            className={input}
            style={{ fontSize: '16px' }}
          />
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        {saved && !error && <p className="text-sm text-green-600">Saved.</p>}
        <button
          onClick={save}
          disabled={saving}
          className="w-full px-4 py-3 bg-gray-900 text-white text-sm font-semibold rounded-xl hover:bg-gray-800 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      <AddressBookCard slug={slug} addresses={addresses} onChanged={onSaved} />
    </div>
  );
}

// ── Address book ──────────────────────────────────────────────────────────────

function AddressBookCard({ slug, addresses, onChanged }: {
  slug: string;
  addresses: PortalAddress[];
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState<PortalAddress | 'new' | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const remove = async (a: PortalAddress) => {
    if (!window.confirm('Delete this address?')) return;
    setBusyId(a.id);
    setError(null);
    try {
      await deletePortalAddress(slug, a.id);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the address');
    } finally {
      setBusyId(null);
    }
  };

  const makeDefault = async (a: PortalAddress, kind: 'shipping' | 'billing') => {
    setBusyId(a.id);
    setError(null);
    try {
      await savePortalAddress(slug, {
        id: a.id,
        label: a.label || undefined,
        address: portalAddressToStructured(a),
        phone: a.phone || undefined,
        deliveryInstructions: a.delivery_instructions || undefined,
        isDefaultShipping: kind === 'shipping' ? true : undefined,
        isDefaultBilling: kind === 'billing' ? true : undefined,
      });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update the address');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
          <MapPin className="h-4 w-4 text-gray-400" /> Addresses
        </h3>
        {!editing && (
          <button
            onClick={() => setEditing('new')}
            className="flex items-center gap-1 text-sm font-semibold text-gray-900 hover:underline"
          >
            <Plus className="h-4 w-4" /> Add address
          </button>
        )}
      </div>

      {addresses.length === 0 && !editing && (
        <p className="text-sm text-gray-500">
          No saved addresses yet. Add one to speed up checkout.
        </p>
      )}

      {!editing && addresses.map(a => {
        const summary = [a.line1, a.line2, a.city, [a.province, a.postal_code].filter(Boolean).join(' '), a.country]
          .filter(Boolean).join(', ');
        const busy = busyId === a.id;
        return (
          <div key={a.id} className="rounded-xl border border-gray-200 px-4 py-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-gray-900">{a.label || a.company || 'Address'}</span>
                  {a.is_default_shipping && (
                    <span className="text-[10px] font-bold uppercase tracking-wide bg-gray-900 text-white rounded-full px-2 py-0.5">Default delivery</span>
                  )}
                  {a.is_default_billing && (
                    <span className="text-[10px] font-bold uppercase tracking-wide bg-gray-100 text-gray-700 rounded-full px-2 py-0.5">Default billing</span>
                  )}
                </div>
                {a.company && a.label && <p className="text-[13px] text-gray-700 mt-0.5">{a.company}</p>}
                <p className="text-[13px] text-gray-500 mt-0.5">{summary}</p>
                {a.phone && <p className="text-[13px] text-gray-500">{a.phone}</p>}
                {a.delivery_instructions && <p className="text-[13px] text-gray-400 italic mt-0.5">{a.delivery_instructions}</p>}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => setEditing(a)} disabled={busy} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500" title="Edit">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => remove(a)} disabled={busy} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600" title="Delete">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {(!a.is_default_shipping || !a.is_default_billing) && (
              <div className="flex gap-3 mt-2">
                {!a.is_default_shipping && (
                  <button onClick={() => makeDefault(a, 'shipping')} disabled={busy} className="text-[13px] font-semibold text-gray-600 hover:text-gray-900 hover:underline">
                    Set as default delivery
                  </button>
                )}
                {!a.is_default_billing && (
                  <button onClick={() => makeDefault(a, 'billing')} disabled={busy} className="text-[13px] font-semibold text-gray-600 hover:text-gray-900 hover:underline">
                    Set as default billing
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}

      {editing && (
        <AddressEditor
          slug={slug}
          existing={editing === 'new' ? null : editing}
          firstAddress={addresses.length === 0}
          onDone={() => {
            setEditing(null);
            onChanged();
          }}
          onCancel={() => setEditing(null)}
        />
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}

function AddressEditor({ slug, existing, firstAddress, onDone, onCancel }: {
  slug: string;
  existing: PortalAddress | null;
  firstAddress: boolean;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [label, setLabel] = useState(existing?.label || '');
  const [address, setAddress] = useState<StructuredAddress>(
    existing ? portalAddressToStructured(existing) : emptyAddress()
  );
  const [phone, setPhone] = useState(existing?.phone || '');
  const [instructions, setInstructions] = useState(existing?.delivery_instructions || '');
  const [defaultShipping, setDefaultShipping] = useState(existing?.is_default_shipping ?? firstAddress);
  const [defaultBilling, setDefaultBilling] = useState(existing?.is_default_billing ?? firstAddress);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!address.line1.trim()) {
      setError('Please enter a street address');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await savePortalAddress(slug, {
        id: existing?.id,
        label: label.trim() || undefined,
        address,
        phone: phone.trim() || undefined,
        deliveryInstructions: instructions.trim() || undefined,
        isDefaultShipping: defaultShipping || undefined,
        isDefaultBilling: defaultBilling || undefined,
      });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the address');
      setSaving(false);
    }
  };

  const input = 'w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900';

  return (
    <div className="rounded-xl border border-gray-300 p-4 space-y-3">
      <h4 className="text-sm font-bold text-gray-900">{existing ? 'Edit address' : 'New address'}</h4>
      <input
        value={label}
        onChange={e => setLabel(e.target.value)}
        placeholder="Label — e.g. Home, Office, Job site (optional)"
        className={input}
        style={{ fontSize: '16px' }}
      />
      <AddressForm value={address} onChange={setAddress} inputClass={input} idPrefix={`portal-ab-${existing?.id || 'new'}`} />
      <input
        value={phone}
        onChange={e => setPhone(e.target.value)}
        placeholder="Phone for this address (optional)"
        className={input}
        style={{ fontSize: '16px' }}
      />
      <textarea
        value={instructions}
        onChange={e => setInstructions(e.target.value)}
        placeholder="Delivery instructions — gate codes, drop-off spot… (optional)"
        rows={2}
        className={`${input} resize-none`}
        style={{ fontSize: '16px' }}
      />
      <div className="space-y-1.5">
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={defaultShipping}
            onChange={e => setDefaultShipping(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 accent-gray-900"
          />
          <span className="text-sm text-gray-700">Default delivery address</span>
        </label>
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={defaultBilling}
            onChange={e => setDefaultBilling(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 accent-gray-900"
          />
          <span className="text-sm text-gray-700">Default billing address</span>
        </label>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button
          onClick={save}
          disabled={saving}
          className="flex-1 px-4 py-2.5 bg-gray-900 text-white text-sm font-semibold rounded-xl hover:bg-gray-800 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {saving ? 'Saving…' : existing ? 'Save address' : 'Add address'}
        </button>
        <button onClick={onCancel} disabled={saving} className="px-4 py-2.5 text-sm font-semibold text-gray-600 hover:text-gray-900 rounded-xl border border-gray-200">
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Shared bits ───────────────────────────────────────────────────────────────

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex justify-between">
      <span className={bold ? 'font-bold text-gray-900' : 'text-gray-500'}>{label}</span>
      <span className={bold ? 'font-bold text-gray-900' : 'text-gray-900'}>{value}</span>
    </div>
  );
}

function EmptyState({ icon: Icon, title, body, action }: {
  icon: React.ElementType;
  title: string;
  body: string;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm py-14 px-6 text-center">
      <div className="mx-auto h-12 w-12 rounded-xl bg-gray-100 flex items-center justify-center mb-3">
        <Icon className="h-6 w-6 text-gray-400" />
      </div>
      <h3 className="text-sm font-bold text-gray-900">{title}</h3>
      <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">{body}</p>
      {action && (
        <button
          onClick={action.onClick}
          className="mt-4 px-4 py-2.5 bg-gray-900 text-white text-sm font-semibold rounded-xl hover:bg-gray-800 transition-colors"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
